import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  CircleStop,
  Clock3,
  Megaphone,
  Play,
  Repeat,
  SlidersHorizontal,
  TriangleAlert,
  Wand2,
  X,
} from 'lucide-react'
import { itemsApi, programsApi } from '../../lib/api.js'
import { adTransitionApi, spotsApi } from '../../lib/adsApi.js'
import { AD_BLOCK_BLACKSCREEN_SEC, adBlockTotalDurationSec } from '../../lib/adBlockTiming.js'
import {
  formatDuration,
  isBroadcastableMedia,
  ITEM_TYPE_LABELS,
  PROGRAM_TYPE_LABELS,
  scheduleItemTitle,
} from '../../lib/utils.js'
import { isoToParisDateTimeInput, parisDateTimeInputToIso } from '../../lib/paris-date-time.js'
import { broadcastDayKey } from '../../lib/broadcast-day.js'
import { recordGridChange } from '../../lib/gridUndo.js'
import type { Program, RebroadcastReport, ScheduleItem } from '../../types/index.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'
import { Button } from '../ui/button.js'
import { fitInfoserviceDurations, suggestRebroadcastCadenceMinutes } from './rebroadcastFit.js'
import { formatAdSpotLabel } from '../../lib/adSpotLabel.js'
import './AgendaDialogs.css'

interface RebroadcastDialogProps {
  items: ScheduleItem[]
  channelId: string
  onDone: (report: RebroadcastReport) => void
  onClose: () => void
}

interface EditableBreak {
  key: string
  afterProgram: ScheduleItem | null
  beforeProgram: ScheduleItem | null
  items: ScheduleItem[]
  startTime: string
  endTime: string
  currentInfoserviceSec: number
  jingles: ScheduleItem[]
}

type TimelineEntry =
  | { kind: 'INFOSERVICE'; key: string }
  | { kind: 'AD_INSERTION'; key: string }
  | { kind: 'ITEM'; key: string; item: ScheduleItem }

type JinglePosition = 'AFTER_PROGRAM' | 'BEFORE_PROGRAM'

interface CatalogAdSpot {
  id: string
  title: string
  durationSec: number
  active: boolean
  campaign?: { id: string; name: string } | null
  advertiser?: { id: string; name: string } | null
}

interface AdInsertionEntry {
  key: string
  spotId: string
}

interface JingleInsertionEntry {
  key: string
  programId: string
}

let adInsertionKey = 0
const newAdInsertionKey = () => `rebroadcast-ad-${++adInsertionKey}`
let jingleInsertionKey = 0
const newJingleInsertionKey = () => `rebroadcast-jingle-${++jingleInsertionKey}`

const isProgramAnchor = (item: ScheduleItem) => item.itemType === 'PROGRAM' || item.itemType === 'LIVE'
const itemTitle = (item: ScheduleItem | null) => item?.program?.title
  ?? item?.liveSource?.name
  ?? (item?.itemType === 'LIVE' ? 'Direct' : 'Programme')

function editableBreaks(items: ScheduleItem[]): EditableBreak[] {
  const ordered = [...items].sort((first, second) => (
    new Date(first.startTime).getTime() - new Date(second.startTime).getTime() || first.sortOrder - second.sortOrder
  ))
  const anchors = ordered.flatMap((item, index) => isProgramAnchor(item) ? [index] : [])
  if (anchors.length === 0) return []

  const result: EditableBreak[] = []
  const push = (afterIndex: number | null, beforeIndex: number | null, start: number, end: number) => {
    const zone = ordered.slice(start, end)
    const afterProgram = afterIndex === null ? null : ordered[afterIndex]
    const beforeProgram = beforeIndex === null ? null : ordered[beforeIndex]
    result.push({
      key: `${afterProgram?.id ?? 'START'}:${beforeProgram?.id ?? 'END'}`,
      afterProgram,
      beforeProgram,
      items: zone,
      startTime: afterProgram?.endTime ?? ordered[0].startTime,
      endTime: beforeProgram?.startTime ?? ordered[ordered.length - 1].endTime,
      currentInfoserviceSec: zone
        .filter(item => item.itemType === 'INFOSERVICE')
        .reduce((total, item) => total + item.durationSec, 0),
      jingles: zone.filter(item => item.itemType === 'JINGLE'),
    })
  }

  push(null, anchors[0], 0, anchors[0])
  for (let index = 0; index < anchors.length - 1; index++) {
    push(anchors[index], anchors[index + 1], anchors[index] + 1, anchors[index + 1])
  }
  const lastAnchor = anchors[anchors.length - 1]
  push(lastAnchor, null, lastAnchor + 1, ordered.length)
  return result
}

function breakLabel(value: EditableBreak): string {
  if (!value.afterProgram) return `Avant « ${itemTitle(value.beforeProgram)} »`
  if (!value.beforeProgram) return `Après « ${itemTitle(value.afterProgram)} »`
  return `Entre « ${itemTitle(value.afterProgram)} » et « ${itemTitle(value.beforeProgram)} »`
}

function hasBroadcastableMedia(program: Program): boolean {
  return Boolean(program.mediaAssets?.some(asset => !asset.isProxy && isBroadcastableMedia(asset.status)))
}

function timelineEntries(value: EditableBreak): TimelineEntry[] {
  const firstInfoserviceIndex = value.items.findIndex(item => item.itemType === 'INFOSERVICE')
  const content = value.items.filter(item => item.itemType !== 'INFOSERVICE')
  let insertAt = 0
  if (firstInfoserviceIndex >= 0) {
    insertAt = value.items.slice(0, firstInfoserviceIndex).filter(item => item.itemType !== 'INFOSERVICE').length
  } else if (value.afterProgram) {
    while (insertAt < content.length && content[insertAt].itemType === 'JINGLE') insertAt += 1
  }
  const entries: TimelineEntry[] = content.map(item => ({ kind: 'ITEM', key: item.id, item }))
  entries.splice(insertAt, 0, { kind: 'INFOSERVICE', key: `infoservice:${value.key}` })
  if (value.afterProgram && value.beforeProgram) {
    let adInsertAt = 0
    while (adInsertAt < entries.length) {
      const entry = entries[adInsertAt]
      if (entry.kind !== 'ITEM' || entry.item.itemType !== 'JINGLE') break
      adInsertAt += 1
    }
    entries.splice(adInsertAt, 0, { kind: 'AD_INSERTION', key: `ads:${value.key}` })
  }
  return entries
}

function insertionKey(value: EditableBreak, position: JinglePosition): string {
  return `${value.key}:${position}`
}

function sourceJingleChoice(item: ScheduleItem): string {
  return item.programId ?? `SOURCE:${item.id}`
}

function edgeJingle(value: EditableBreak, position: JinglePosition): ScheduleItem | null {
  if (position === 'AFTER_PROGRAM' && !value.afterProgram) return null
  if (position === 'BEFORE_PROGRAM' && !value.beforeProgram) return null

  const infoserviceIndexes = value.items.flatMap((item, index) => item.itemType === 'INFOSERVICE' ? [index] : [])
  if (infoserviceIndexes.length > 0) {
    const boundaryIndex = position === 'AFTER_PROGRAM'
      ? infoserviceIndexes[0]
      : infoserviceIndexes[infoserviceIndexes.length - 1]
    const candidates = value.items.filter((item, index) => (
      item.itemType === 'JINGLE'
      && (position === 'AFTER_PROGRAM' ? index < boundaryIndex : index > boundaryIndex)
    ))
    return position === 'AFTER_PROGRAM' ? candidates[0] ?? null : candidates[candidates.length - 1] ?? null
  }

  // Sans infoservice source, un jingle unique appartient au début de l'écart.
  // Il ne doit pas masquer en même temps le sélecteur indépendant de fin.
  if (position === 'AFTER_PROGRAM') return value.jingles[0] ?? null
  if (!value.afterProgram) return value.jingles[value.jingles.length - 1] ?? null
  return value.jingles.length > 1 ? value.jingles[value.jingles.length - 1] ?? null : null
}

function exactDuration(totalSec: number): string {
  const safe = Math.max(0, Math.round(totalSec))
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const seconds = safe % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function antennaMoment(iso: string | null, reference?: string): string {
  if (!iso) return '—'
  const at = new Date(iso)
  const clock = at.toLocaleTimeString('fr-FR', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Paris',
  })
  if (!reference) return clock
  const date = at.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Paris' })
  const referenceDate = new Date(reference).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', timeZone: 'Europe/Paris',
  })
  return date === referenceDate ? clock : `${date} · ${clock}`
}

function sourceBreakDuration(value: EditableBreak): number {
  return Math.max(0, Math.round((new Date(value.endTime).getTime() - new Date(value.startTime).getTime()) / 1000))
}

function editedBreakDuration(
  value: EditableBreak,
  infoserviceSec: number,
  jingleChoices: Record<string, string>,
  jingleInsertions: Record<string, JingleInsertionEntry[]>,
  jingles: Program[],
  insertedAdDurationSec: number,
): number {
  const jingleDelta = value.jingles.reduce((total, item) => {
    const choice = jingleChoices[item.id]
    if (choice === '') return total - item.durationSec
    const target = jingles.find(program => program.id === choice)
    return total + (target ? target.durationSec - item.durationSec : 0)
  }, 0)
  const insertionDuration = (['AFTER_PROGRAM', 'BEFORE_PROGRAM'] as const).reduce((total, position) => {
    const entries = jingleInsertions[insertionKey(value, position)] ?? []
    return total + entries.reduce((sum, entry) => (
      sum + (jingles.find(program => program.id === entry.programId)?.durationSec ?? 0)
    ), 0)
  }, 0)
  if (infoserviceSec === value.currentInfoserviceSec && insertionDuration === 0 && insertedAdDurationSec === 0) {
    return Math.max(0, sourceBreakDuration(value) + jingleDelta + insertionDuration + insertedAdDurationSec)
  }
  const retainedItemsSec = value.items
    .filter(item => infoserviceSec === value.currentInfoserviceSec || item.itemType !== 'INFOSERVICE')
    .reduce((total, item) => total + item.durationSec, 0)
  const adjustedInfoserviceSec = infoserviceSec === value.currentInfoserviceSec ? 0 : infoserviceSec
  return Math.max(0, retainedItemsSec + jingleDelta + insertionDuration + insertedAdDurationSec + adjustedInfoserviceSec)
}

function jingleRole(value: EditableBreak, item: ScheduleItem): string {
  if (edgeJingle(value, 'AFTER_PROGRAM')?.id === item.id) return 'Élément d’antenne après le programme'
  if (edgeJingle(value, 'BEFORE_PROGRAM')?.id === item.id) return 'Élément d’antenne avant le programme'
  const index = value.jingles.findIndex(candidate => candidate.id === item.id)
  return `Élément d’antenne ${index + 1}`
}

export const RebroadcastDialog = memo(function RebroadcastDialog({ items, channelId, onDone, onClose }: RebroadcastDialogProps) {
  const span = useMemo(() => {
    const start = Math.min(...items.map(item => new Date(item.startTime).getTime()))
    const end = Math.max(...items.map(item => new Date(item.endTime).getTime()))
    return { start: new Date(start), end: new Date(end), durationSec: Math.round((end - start) / 1000) }
  }, [items])

  // La sélection peut finir à HH:mm:ss. L'arrondir à la minute ferait repartir
  // la première copie quelques secondes trop tôt, donc en conflit avec sa
  // propre source (00:01:02 devenait 00:01:00 dans le dialogue).
  const [targetStart, setTargetStart] = useState(() => isoToParisDateTimeInput(span.end.toISOString(), true))
  const [repeatUntil, setRepeatUntil] = useState(() => {
    const next = new Date(span.end)
    next.setDate(next.getDate() + 1)
    next.setHours(6, 0, 0, 0)
    return isoToParisDateTimeInput(next.toISOString())
  })
  const [replaceExisting, setReplaceExisting] = useState(false)
  const [chainAfterLast, setChainAfterLast] = useState(false)
  const [partialTail, setPartialTail] = useState(false)
  const [keepTimeMarkers, setKeepTimeMarkers] = useState(false)
  const [cadenceMinutes, setCadenceMinutes] = useState(() => suggestRebroadcastCadenceMinutes(span.durationSec))
  const [preview, setPreview] = useState<RebroadcastReport | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const breaks = useMemo(() => editableBreaks(items), [items])
  const [breakIndex, setBreakIndex] = useState(0)
  const [fitNote, setFitNote] = useState<string | null>(null)
  const [infoserviceDurations, setInfoserviceDurations] = useState<Record<string, number>>(() => (
    Object.fromEntries(editableBreaks(items).map(value => [value.key, value.currentInfoserviceSec]))
  ))
  const [jingleChoices, setJingleChoices] = useState<Record<string, string>>(() => Object.fromEntries(
    items.filter(item => item.itemType === 'JINGLE').map(item => [item.id, sourceJingleChoice(item)]),
  ))
  const [jingleInsertions, setJingleInsertions] = useState<Record<string, JingleInsertionEntry[]>>({})
  const [jingles, setJingles] = useState<Program[]>([])
  const [jinglesLoading, setJinglesLoading] = useState(true)
  const [adInsertions, setAdInsertions] = useState<Record<string, AdInsertionEntry[]>>({})
  const [adSpots, setAdSpots] = useState<CatalogAdSpot[]>([])
  const [adSpotsLoading, setAdSpotsLoading] = useState(true)
  const [adTransitionMs, setAdTransitionMs] = useState(AD_BLOCK_BLACKSCREEN_SEC * 1000)

  useEffect(() => {
    let cancelled = false
    programsApi.list({ channelId, catalog: 'jingles', limit: 100, sortBy: 'title', sortDir: 'asc' })
      .then(result => {
        if (!cancelled) setJingles((result.data ?? []).filter((program: Program) => !program.isLive && hasBroadcastableMedia(program)))
      })
      .catch(cause => {
        if (!cancelled) setError(cause?.message ?? 'Impossible de charger les éléments d’antenne.')
      })
      .finally(() => { if (!cancelled) setJinglesLoading(false) })
    return () => { cancelled = true }
  }, [channelId])

  useEffect(() => {
    let cancelled = false
    setAdSpotsLoading(true)
    Promise.all([
      spotsApi.list({ channelId }),
      adTransitionApi.get(channelId).catch(() => null),
    ]).then(([result, transition]) => {
      if (cancelled) return
      setAdSpots((Array.isArray(result) ? result : []).filter((spot: CatalogAdSpot) => spot.active && spot.campaign))
      if (transition) {
        setAdTransitionMs(transition.mode === 'VIDEO' ? transition.videoDurationMs : transition.blackDurationMs)
      }
    }).catch(cause => {
      if (!cancelled) setError(cause?.message ?? 'Impossible de charger les spots publicitaires.')
    }).finally(() => { if (!cancelled) setAdSpotsLoading(false) })
    return () => { cancelled = true }
  }, [channelId])

  useEffect(() => {
    setBreakIndex(current => Math.min(current, Math.max(0, breaks.length - 1)))
  }, [breaks.length])

  const repeatUntilIso = useMemo(() => parisDateTimeInputToIso(repeatUntil), [repeatUntil])
  const itemIds = useMemo(() => items.map(item => item.id), [items])
  const montage = useMemo(() => ({
    breakAdjustments: breaks.flatMap(value => {
      const duration = infoserviceDurations[value.key] ?? value.currentInfoserviceSec
      return duration === value.currentInfoserviceSec ? [] : [{
        afterProgramItemId: value.afterProgram?.id ?? null,
        beforeProgramItemId: value.beforeProgram?.id ?? null,
        infoserviceDurationSec: duration,
      }]
    }),
    jingleReplacements: breaks.flatMap(value => value.jingles).flatMap(item => {
      const programId = jingleChoices[item.id]
      return !programId || programId === sourceJingleChoice(item) || programId.startsWith('SOURCE:')
        ? []
        : [{ sourceItemId: item.id, programId }]
    }),
    jingleRemovals: breaks.flatMap(value => value.jingles).flatMap(item => (
      jingleChoices[item.id] === '' ? [{ sourceItemId: item.id }] : []
    )),
    jingleInsertions: breaks.flatMap(value => (['AFTER_PROGRAM', 'BEFORE_PROGRAM'] as const).flatMap(position => (
      (jingleInsertions[insertionKey(value, position)] ?? []).flatMap(entry => entry.programId ? [{
        afterProgramItemId: value.afterProgram?.id ?? null,
        beforeProgramItemId: value.beforeProgram?.id ?? null,
        position,
        programId: entry.programId,
      }] : [])
    ))),
    adInsertions: breaks.flatMap(value => {
      if (!value.afterProgram || !value.beforeProgram) return []
      const spotIds = (adInsertions[value.key] ?? []).map(entry => entry.spotId)
      return spotIds.length > 0 ? [{
        afterProgramItemId: value.afterProgram.id,
        beforeProgramItemId: value.beforeProgram.id,
        spotIds,
      }] : []
    }),
  }), [adInsertions, breaks, infoserviceDurations, jingleChoices, jingleInsertions])

  const targetStartIso = useMemo(
    () => (chainAfterLast ? span.end.toISOString() : parisDateTimeInputToIso(targetStart)),
    [chainAfterLast, span.end, targetStart],
  )
  const windowValid = Boolean(targetStartIso) && Boolean(repeatUntilIso)
    && (chainAfterLast || new Date(repeatUntilIso).getTime() > new Date(targetStartIso).getTime())
    && (!keepTimeMarkers || cadenceMinutes > 0)

  const cadenceRequest = keepTimeMarkers ? { cadenceSec: cadenceMinutes * 60 } : {}

  useEffect(() => {
    if (!windowValid) {
      setPreview(null)
      return
    }
    let cancelled = false
    setPreviewing(true)
    const timer = window.setTimeout(() => {
      itemsApi.rebroadcast({
        itemIds,
        targetStart: targetStartIso,
        repeatUntil: repeatUntilIso,
        replaceExisting,
        startMode: chainAfterLast ? 'AFTER_LAST_ITEM' : 'EXPLICIT',
        partialTail,
        ...cadenceRequest,
        ...montage,
        dryRun: true,
      }).then(report => {
        if (cancelled) return
        setPreview(report)
        setError(null)
      }).catch(cause => {
        if (cancelled) return
        setPreview(null)
        setError(cause?.message ?? 'Impossible de simuler la rediffusion.')
      }).finally(() => {
        if (!cancelled) setPreviewing(false)
      })
    }, 300)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [cadenceMinutes, chainAfterLast, itemIds, keepTimeMarkers, montage, partialTail, targetStartIso, repeatUntilIso, replaceExisting, windowValid])

  const submit = useCallback(async () => {
    if (!windowValid || !preview || preview.occurrences === 0) return
    setBusy(true)
    setError(null)
    try {
      // La boucle peut déborder sur la nuit suivante, donc sur la grille de la
      // semaine d'après : la photo « avant » couvre jusqu'à l'heure de fin
      // demandée, et le rapport dit après coup quelles grilles ont servi.
      const report = await recordGridChange({
        label:   'rediffusion',
        gridIds: items.map(item => item.gridId),
        date:    broadcastDayKey(targetStartIso),
        until:   repeatUntilIso,
        gridIdsFromResult: (applied: RebroadcastReport) => applied.gridIds,
      }, () => itemsApi.rebroadcast({
        itemIds,
        targetStart: targetStartIso,
        repeatUntil: repeatUntilIso,
        replaceExisting,
        startMode: chainAfterLast ? 'AFTER_LAST_ITEM' : 'EXPLICIT',
        partialTail,
        ...cadenceRequest,
        ...montage,
      }))
      onDone(report)
      onClose()
    } catch (cause: any) {
      setError(cause?.message ?? 'Impossible de rediffuser cette plage.')
    } finally {
      setBusy(false)
    }
  }, [cadenceMinutes, chainAfterLast, items, itemIds, keepTimeMarkers, montage, onClose, onDone, partialTail, preview, repeatUntilIso, replaceExisting, targetStartIso, windowValid])

  /**
   * Cale la boucle sur la fin souhaitée en jouant sur les seuls infoservices.
   *
   * On garde le nombre de passages que le serveur a retenu et on répartit le
   * reliquat — « Reste à remplir » — sur les écarts. Les programmes ne bougent
   * pas : c'est bien l'habillage qui s'étire, pas la grille qui se décale.
   */
  const autoFitInfoservice = useCallback(() => {
    setFitNote(null)
    if (!preview || preview.occurrences === 0) return
    const startMs = new Date(preview.targetStart).getTime()
    const endMs   = new Date(repeatUntilIso).getTime()
    const windowSec = Math.floor((endMs - startMs) / 1000)
    if (!Number.isFinite(windowSec) || windowSec <= 0) return

    const desiredLoopSec = Math.floor(windowSec / preview.occurrences)
    const deltaSec = desiredLoopSec - preview.sourceDurationSec
    if (deltaSec === 0) {
      setFitNote('La boucle tombe déjà juste : rien à ajuster.')
      return
    }

    const { durations, residualSec } = fitInfoserviceDurations(breaks, infoserviceDurations, deltaSec)
    setInfoserviceDurations(durations)
    setFitNote(residualSec === 0
      ? null
      : `Ajustement partiel : ${exactDuration(Math.abs(residualSec))} n’ont pas pu être absorbés sans passer sous la minute d’infoservice.`)
  }, [breaks, infoserviceDurations, preview, repeatUntilIso])

  const previousBreak = useCallback(() => setBreakIndex(current => Math.max(0, current - 1)), [])
  const nextBreak = useCallback(() => setBreakIndex(current => Math.min(breaks.length - 1, current + 1)), [breaks.length])
  const addAdSpot = useCallback((breakKey: string, spotId: string) => {
    if (!spotId) return
    setAdInsertions(current => ({
      ...current,
      [breakKey]: [...(current[breakKey] ?? []), { key: newAdInsertionKey(), spotId }],
    }))
  }, [])
  const moveAdSpot = useCallback((breakKey: string, rowKey: string, direction: -1 | 1) => {
    setAdInsertions(current => {
      const entries = [...(current[breakKey] ?? [])]
      const index = entries.findIndex(entry => entry.key === rowKey)
      const target = index + direction
      if (index < 0 || target < 0 || target >= entries.length) return current
      const moved = entries[index]
      entries[index] = entries[target]
      entries[target] = moved
      return { ...current, [breakKey]: entries }
    })
  }, [])
  const removeAdSpot = useCallback((breakKey: string, rowKey: string) => {
    setAdInsertions(current => ({
      ...current,
      [breakKey]: (current[breakKey] ?? []).filter(entry => entry.key !== rowKey),
    }))
  }, [])
  const addJingleInsertion = useCallback((key: string, programId: string) => {
    if (!programId) return
    setJingleInsertions(current => ({
      ...current,
      [key]: [...(current[key] ?? []), { key: newJingleInsertionKey(), programId }],
    }))
  }, [])
  const moveJingleInsertion = useCallback((key: string, rowKey: string, direction: -1 | 1) => {
    setJingleInsertions(current => {
      const entries = [...(current[key] ?? [])]
      const index = entries.findIndex(entry => entry.key === rowKey)
      const target = index + direction
      if (index < 0 || target < 0 || target >= entries.length) return current
      const moved = entries[index]
      entries[index] = entries[target]
      entries[target] = moved
      return { ...current, [key]: entries }
    })
  }, [])
  const removeJingleInsertion = useCallback((key: string, rowKey: string) => {
    setJingleInsertions(current => ({
      ...current,
      [key]: (current[key] ?? []).filter(entry => entry.key !== rowKey),
    }))
  }, [])
  const activeBreak = breaks[breakIndex] ?? null
  const activeInfoserviceSec = activeBreak ? infoserviceDurations[activeBreak.key] ?? 0 : 0
  const activeSourceDuration = activeBreak ? sourceBreakDuration(activeBreak) : 0
  const activeAdEntries = activeBreak ? adInsertions[activeBreak.key] ?? [] : []
  const activeAdDuration = adBlockTotalDurationSec(
    activeAdEntries.map(entry => adSpots.find(spot => spot.id === entry.spotId)?.durationSec ?? 0),
    adTransitionMs,
  )
  const activeEditedDuration = activeBreak
    ? editedBreakDuration(activeBreak, activeInfoserviceSec, jingleChoices, jingleInsertions, jingles, activeAdDuration)
    : 0
  const activeOptionCount = Number(chainAfterLast) + Number(partialTail) + Number(replaceExisting) + Number(keepTimeMarkers)

  return (
    <ModalShell
      title="Rediffuser une plage"
      onClose={onClose}
      closeDisabled={busy}
      size="medium"
      className="agenda-dialog agenda-dialog--rebroadcast"
    >
      <div className="agenda-dialog__subtitle">
        {items.length} élément{items.length > 1 ? 's' : ''} · {antennaMoment(span.start.toISOString())} → {antennaMoment(span.end.toISOString(), span.start.toISOString())} · {exactDuration(preview?.sourceDurationSec ?? span.durationSec)} par boucle
      </div>

      <div className="agenda-rebroadcast__body">
        <div className="agenda-rebroadcast__window">
          <label className="agenda-rebroadcast__field">
            <span className="agenda-dialog__section-label agenda-dialog__section-label--icon">
              <Repeat size={13} aria-hidden="true" /> Rediffuser à partir de
            </span>
            <input
              type="datetime-local"
              step="1"
              className="tp-input"
              value={targetStart}
              disabled={busy || chainAfterLast}
              onChange={event => setTargetStart(event.target.value)}
            />
            {chainAfterLast && (
              <em className="agenda-rebroadcast__resolved">
                {preview ? <>Départ retenu : {antennaMoment(preview.targetStart)}</> : <>Départ calculé par le serveur…</>}
              </em>
            )}
          </label>

          <label className="agenda-rebroadcast__field">
            <span className="agenda-dialog__section-label agenda-dialog__section-label--icon">
              <CalendarClock size={13} aria-hidden="true" /> Fin souhaitée
            </span>
            <input
              type="datetime-local"
              className="tp-input"
              value={repeatUntil}
              disabled={busy}
              onChange={event => setRepeatUntil(event.target.value)}
            />
          </label>

          <div className="agenda-rebroadcast__fit">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={autoFitInfoservice}
              disabled={busy || keepTimeMarkers || previewing || !preview || preview.occurrences === 0}
            >
              <Wand2 data-icon="inline-start" aria-hidden="true" />
              Caler sur la fin souhaitée
            </Button>
            <small>
              {keepTimeMarkers
                ? 'Les horaires repères remplissent automatiquement chaque attente avec l’infoservice sélectionné pour la chaîne.'
                : fitNote
                ?? (preview && preview.shortfallSec > 0
                  ? `Étire les infoservices des écarts pour absorber les ${exactDuration(preview.shortfallSec)} restants, sans descendre sous une minute par écart.`
                  : 'Répartit le reliquat sur les infoservices des écarts pour que la dernière boucle finisse pile à l’heure.')}
            </small>
          </div>
        </div>

        {activeBreak ? (
          <section className="agenda-rebroadcast__montage" aria-labelledby="rebroadcast-gap-title">
            <header className="agenda-rebroadcast__gap-header">
              <div className="agenda-rebroadcast__gap-heading">
                <span className="agenda-dialog__section-label agenda-dialog__section-label--icon">
                  <SlidersHorizontal size={13} aria-hidden="true" /> Aperçu de l’écart
                </span>
                <strong id="rebroadcast-gap-title">{breakLabel(activeBreak)}</strong>
                <span>
                  Écart source {exactDuration(activeSourceDuration)}
                  {activeEditedDuration !== activeSourceDuration && <> · après réglage {exactDuration(activeEditedDuration)}</>}
                </span>
              </div>
              <nav className="agenda-rebroadcast__gap-nav" aria-label="Navigation entre les écarts">
                <Button type="button" variant="outline" size="icon-sm" onClick={previousBreak} disabled={breakIndex === 0 || busy} aria-label="Écart précédent">
                  <ChevronLeft aria-hidden="true" />
                </Button>
                <span aria-live="polite">Écart {breakIndex + 1} sur {breaks.length}</span>
                <Button type="button" variant="outline" size="icon-sm" onClick={nextBreak} disabled={breakIndex === breaks.length - 1 || busy} aria-label="Écart suivant">
                  <ChevronRight aria-hidden="true" />
                </Button>
              </nav>
            </header>

            <div className="agenda-rebroadcast__timeline">
              <GapBoundary
                icon="END"
                label={activeBreak.afterProgram ? `${itemTitle(activeBreak.afterProgram)} · fin` : 'Début de la sélection'}
                time={antennaMoment(activeBreak.startTime)}
              />

              <div className="agenda-rebroadcast__timeline-content">
                {activeBreak.afterProgram && (
                  <JingleInsertionTimelineRow
                    role="Éléments d’antenne ajoutés après le programme"
                    entries={jingleInsertions[insertionKey(activeBreak, 'AFTER_PROGRAM')] ?? []}
                    choices={jingles}
                    disabled={busy || jinglesLoading}
                    loading={jinglesLoading}
                    onAdd={programId => addJingleInsertion(insertionKey(activeBreak, 'AFTER_PROGRAM'), programId)}
                    onMove={(rowKey, direction) => moveJingleInsertion(insertionKey(activeBreak, 'AFTER_PROGRAM'), rowKey, direction)}
                    onRemove={rowKey => removeJingleInsertion(insertionKey(activeBreak, 'AFTER_PROGRAM'), rowKey)}
                  />
                )}
                {timelineEntries(activeBreak).map(entry => {
                  if (entry.kind === 'INFOSERVICE') {
                    return <InfoserviceTimelineRow
                      key={entry.key}
                      durationSec={activeInfoserviceSec}
                      disabled={busy}
                      onChange={durationSec => setInfoserviceDurations(current => ({ ...current, [activeBreak.key]: durationSec }))}
                    />
                  }
                  if (entry.kind === 'AD_INSERTION') {
                    return <AdInsertionTimelineRow
                      key={entry.key}
                      entries={activeAdEntries}
                      choices={adSpots}
                      transitionDurationMs={adTransitionMs}
                      disabled={busy || adSpotsLoading}
                      loading={adSpotsLoading}
                      onAdd={spotId => addAdSpot(activeBreak.key, spotId)}
                      onMove={(rowKey, direction) => moveAdSpot(activeBreak.key, rowKey, direction)}
                      onRemove={rowKey => removeAdSpot(activeBreak.key, rowKey)}
                    />
                  }
                  return entry.item.itemType === 'JINGLE'
                    ? <JingleTimelineRow
                        key={entry.key}
                        item={entry.item}
                        role={jingleRole(activeBreak, entry.item)}
                        choices={jingles}
                        value={jingleChoices[entry.item.id] ?? sourceJingleChoice(entry.item)}
                        disabled={busy || jinglesLoading}
                        onChange={programId => setJingleChoices(current => ({ ...current, [entry.item.id]: programId }))}
                      />
                    : <ReadOnlyTimelineRow key={entry.key} item={entry.item} />
                })}
                {activeBreak.beforeProgram && (
                  <JingleInsertionTimelineRow
                    role="Éléments d’antenne ajoutés avant le programme"
                    entries={jingleInsertions[insertionKey(activeBreak, 'BEFORE_PROGRAM')] ?? []}
                    choices={jingles}
                    disabled={busy || jinglesLoading}
                    loading={jinglesLoading}
                    onAdd={programId => addJingleInsertion(insertionKey(activeBreak, 'BEFORE_PROGRAM'), programId)}
                    onMove={(rowKey, direction) => moveJingleInsertion(insertionKey(activeBreak, 'BEFORE_PROGRAM'), rowKey, direction)}
                    onRemove={rowKey => removeJingleInsertion(insertionKey(activeBreak, 'BEFORE_PROGRAM'), rowKey)}
                  />
                )}
              </div>

              <GapBoundary
                icon="START"
                label={activeBreak.beforeProgram ? `${itemTitle(activeBreak.beforeProgram)} · début` : 'Fin de la sélection'}
                time={antennaMoment(activeBreak.endTime, activeBreak.startTime)}
              />
            </div>
          </section>
        ) : (
          <div className="agenda-dialog__notice is-warning">La sélection ne contient aucun programme permettant de délimiter les écarts.</div>
        )}

        <details className="agenda-rebroadcast__advanced">
          <summary>Options avancées{activeOptionCount > 0 ? ` · ${activeOptionCount} active${activeOptionCount > 1 ? 's' : ''}` : ''}</summary>
          <div className="agenda-rebroadcast__advanced-content">
            <div className="agenda-rebroadcast__cadence-option">
              <label className="agenda-rebroadcast__replace">
                <input type="checkbox" checked={keepTimeMarkers} disabled={busy} onChange={event => setKeepTimeMarkers(event.target.checked)} />
                <span>
                  Conserver les horaires ronds
                  <em>Chaque passage repart selon une cadence globale, sans dériver avec la durée réelle du montage.</em>
                </span>
              </label>
              {keepTimeMarkers && (
                <label className="agenda-rebroadcast__cadence-control">
                  <span>Cadence globale</span>
                  <input
                    aria-label="Cadence globale en minutes"
                    className="tp-input tp-input--data"
                    type="number"
                    min="1"
                    max="1440"
                    value={cadenceMinutes}
                    disabled={busy}
                    onChange={event => setCadenceMinutes(Math.min(1440, Math.max(1, Math.round(Number(event.target.value || 1)))))}
                  />
                  <span>minutes</span>
                  <em>L’infoservice complète automatiquement jusqu’au repère suivant et jusqu’à la fin souhaitée.</em>
                </label>
              )}
            </div>
            <label className="agenda-rebroadcast__replace">
              <input type="checkbox" checked={chainAfterLast} disabled={busy} onChange={event => setChainAfterLast(event.target.checked)} />
              <span>
                Enchaîner après le dernier élément déjà posé
                <em>Le serveur choisit le premier vrai temps libre situé après la sélection.</em>
              </span>
            </label>
            <label className="agenda-rebroadcast__replace">
              <input type="checkbox" checked={partialTail} disabled={busy} onChange={event => setPartialTail(event.target.checked)} />
              <span>
                Écourter la dernière boucle
                <em>Seuls les éléments entiers qui tiennent avant la fin souhaitée sont repris.</em>
              </span>
            </label>
            <label className="agenda-rebroadcast__replace">
              <input type="checkbox" checked={replaceExisting} disabled={busy} onChange={event => setReplaceExisting(event.target.checked)} />
              <span>
                Remplacer les éléments déjà posés
                <em>Les éléments chargés, à l’antenne ou déjà diffusés restent protégés.</em>
              </span>
            </label>
          </div>
        </details>

        {!windowValid && <div className="agenda-dialog__notice is-warning">La fin souhaitée doit être postérieure au début de la rediffusion.</div>}
        {error && <div role="alert" className="agenda-dialog__error">{error}</div>}
      </div>

      <footer className="agenda-rebroadcast__footer">
        <RebroadcastOutcome report={preview} loading={previewing} windowValid={windowValid} partialAllowed={partialTail} />
        <div className="tp-modal-actions agenda-rebroadcast__actions">
          <Button type="button" variant="outline" disabled={busy} onClick={onClose}>Annuler</Button>
          <Button
            type="button"
            disabled={busy || previewing || !preview || preview.occurrences === 0}
            data-state={busy ? 'loading' : undefined}
            onClick={() => { submit().catch(console.error) }}
          >
            {busy ? 'Rediffusion…' : preview?.occurrences ? `Rediffuser ${preview.occurrences} fois` : 'Rediffuser'}
          </Button>
        </div>
      </footer>
    </ModalShell>
  )
})

const GapBoundary = memo(function GapBoundary({ icon, label, time }: {
  icon: 'START' | 'END'
  label: string
  time: string
}) {
  const Icon = icon === 'END' ? CircleStop : Play
  return (
    <div className={`agenda-rebroadcast__boundary is-${icon.toLowerCase()}`}>
      <span><Icon aria-hidden="true" /> {label}</span>
      <time>{time}</time>
    </div>
  )
})

const InfoserviceTimelineRow = memo(function InfoserviceTimelineRow({ durationSec, disabled, onChange }: {
  durationSec: number
  disabled: boolean
  onChange: (durationSec: number) => void
}) {
  const minutes = Math.floor(durationSec / 60)
  const seconds = durationSec % 60
  return (
    <div className={`agenda-rebroadcast__timeline-row is-infoservice${durationSec === 0 ? ' is-empty' : ''}`}>
      <div className="agenda-rebroadcast__timeline-row-main">
        <strong>Infoservice</strong>
        <span>{durationSec === 0 ? 'Aucun infoservice — renseignez une durée pour en ajouter un' : `Durée totale ${exactDuration(durationSec)}`}</span>
      </div>
      <div className="agenda-rebroadcast__duration-fields" aria-label="Durée de l’infoservice">
        <label>
          <input aria-label="Minutes d’infoservice" className="tp-input tp-input--data" type="number" min="0" max="360" value={minutes} disabled={disabled} onChange={event => onChange(Math.max(0, Number(event.target.value || 0)) * 60 + seconds)} />
          <span>min</span>
        </label>
        <label>
          <input aria-label="Secondes d’infoservice" className="tp-input tp-input--data" type="number" min="0" max="59" value={seconds} disabled={disabled} onChange={event => onChange(minutes * 60 + Math.min(59, Math.max(0, Number(event.target.value || 0))))} />
          <span>sec</span>
        </label>
      </div>
    </div>
  )
})

const AdInsertionTimelineRow = memo(function AdInsertionTimelineRow({
  entries,
  choices,
  transitionDurationMs,
  disabled,
  loading,
  onAdd,
  onMove,
  onRemove,
}: {
  entries: AdInsertionEntry[]
  choices: CatalogAdSpot[]
  transitionDurationMs: number
  disabled: boolean
  loading: boolean
  onAdd: (spotId: string) => void
  onMove: (rowKey: string, direction: -1 | 1) => void
  onRemove: (rowKey: string) => void
}) {
  const selected = entries.flatMap(entry => {
    const spot = choices.find(choice => choice.id === entry.spotId)
    return spot ? [{ ...entry, spot }] : []
  })
  const durationSec = adBlockTotalDurationSec(selected.map(entry => entry.spot.durationSec), transitionDurationMs)

  return (
    <div className={`agenda-rebroadcast__timeline-row is-ad${entries.length === 0 ? ' is-empty' : ''}`}>
      <div className="agenda-rebroadcast__timeline-row-main">
        <strong><Megaphone aria-hidden="true" /> Publicités ajoutées</strong>
        <span>{entries.length === 0
          ? 'Aucune — composez un bloc propre à chaque copie'
          : `${entries.length} spot${entries.length > 1 ? 's' : ''} · durée totale ${exactDuration(durationSec)}`}</span>
      </div>
      <div className="agenda-rebroadcast__ad-editor">
        {selected.length > 0 && (
          <ol className="agenda-rebroadcast__ad-list" aria-label="Spots publicitaires ajoutés">
            {selected.map((entry, index) => (
              <li key={entry.key}>
                <span><strong>{formatAdSpotLabel(entry.spot)}</strong><small>{formatDuration(entry.spot.durationSec)}</small></span>
                <span className="agenda-rebroadcast__ad-actions">
                  <Button type="button" variant="ghost" size="icon-xs" disabled={disabled || index === 0} onClick={() => onMove(entry.key, -1)} aria-label={`Monter ${formatAdSpotLabel(entry.spot)}`}>
                    <ArrowUp aria-hidden="true" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon-xs" disabled={disabled || index === selected.length - 1} onClick={() => onMove(entry.key, 1)} aria-label={`Descendre ${formatAdSpotLabel(entry.spot)}`}>
                    <ArrowDown aria-hidden="true" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon-xs" disabled={disabled} onClick={() => onRemove(entry.key)} aria-label={`Retirer ${formatAdSpotLabel(entry.spot)}`}>
                    <X aria-hidden="true" />
                  </Button>
                </span>
              </li>
            ))}
          </ol>
        )}
        <select
          aria-label="Ajouter un spot publicitaire"
          className="tp-select"
          value=""
          disabled={disabled || choices.length === 0 || entries.length >= 100}
          onChange={event => onAdd(event.target.value)}
        >
          <option value="">{loading
            ? 'Chargement des spots…'
            : choices.length === 0
              ? 'Aucun spot disponible'
              : entries.length >= 100
                ? 'Limite de 100 spots atteinte'
                : 'Ajouter un spot…'}</option>
          {choices.map(spot => (
            <option key={spot.id} value={spot.id}>
              {formatAdSpotLabel(spot)} · {formatDuration(spot.durationSec)}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
})

const JingleTimelineRow = memo(function JingleTimelineRow({ item, role, choices, value, disabled, onChange }: {
  item: ScheduleItem
  role: string
  choices: Program[]
  value: string
  disabled: boolean
  onChange: (programId: string) => void
}) {
  const selected = choices.find(program => program.id === value)
  const sourceValue = sourceJingleChoice(item)
  return (
    <div className={`agenda-rebroadcast__timeline-row is-jingle${value ? '' : ' is-empty'}`}>
      <div className="agenda-rebroadcast__timeline-row-main">
        <strong>{role}</strong>
        <span>{value === ''
          ? 'Supprimé de la rediffusion — l’élément source reste inchangé'
          : selected
            ? `${PROGRAM_TYPE_LABELS[selected.type]} · ${formatDuration(selected.durationSec)}`
            : `Élément d’antenne actuel · ${formatDuration(item.durationSec)}`}</span>
      </div>
      <select aria-label={role} className="tp-select" value={value} disabled={disabled} onChange={event => onChange(event.target.value)}>
        <option value="">Aucun élément d’antenne — supprimer</option>
        {!choices.some(program => program.id === sourceValue) && (
          <option value={sourceValue}>{item.program?.title ?? 'Élément d’antenne actuel'} · {formatDuration(item.durationSec)}</option>
        )}
        {choices.map(program => (
          <option key={program.id} value={program.id}>{program.title} · {formatDuration(program.durationSec)}</option>
        ))}
      </select>
    </div>
  )
})

const JingleInsertionTimelineRow = memo(function JingleInsertionTimelineRow({
  role,
  entries,
  choices,
  disabled,
  loading,
  onAdd,
  onMove,
  onRemove,
}: {
  role: string
  entries: JingleInsertionEntry[]
  choices: Program[]
  disabled: boolean
  loading: boolean
  onAdd: (programId: string) => void
  onMove: (rowKey: string, direction: -1 | 1) => void
  onRemove: (rowKey: string) => void
}) {
  const selected = entries.flatMap(entry => {
    const program = choices.find(choice => choice.id === entry.programId)
    return program ? [{ ...entry, program }] : []
  })
  const durationSec = selected.reduce((total, entry) => total + entry.program.durationSec, 0)
  return (
    <div className={`agenda-rebroadcast__timeline-row is-jingle${entries.length === 0 ? ' is-empty' : ''}`}>
      <div className="agenda-rebroadcast__timeline-row-main">
        <strong>{role}</strong>
        <span>{selected.length > 0
          ? `${selected.length} élément${selected.length > 1 ? 's' : ''} · durée totale ${exactDuration(durationSec)}`
          : 'Aucun — ajoutez les habillages à rejouer dans chaque copie'}</span>
      </div>
      <div className="agenda-rebroadcast__jingle-editor">
        {selected.length > 0 && (
          <ol className="agenda-rebroadcast__jingle-list" aria-label={role}>
            {selected.map((entry, index) => (
              <li key={entry.key}>
                <span>
                  <strong>{entry.program.title}</strong>
                  <small>{PROGRAM_TYPE_LABELS[entry.program.type]} · {formatDuration(entry.program.durationSec)}</small>
                </span>
                <span className="agenda-rebroadcast__jingle-actions">
                  <Button type="button" variant="ghost" size="icon-xs" disabled={disabled || index === 0} onClick={() => onMove(entry.key, -1)} aria-label={`Monter ${entry.program.title}`}>
                    <ArrowUp aria-hidden="true" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon-xs" disabled={disabled || index === selected.length - 1} onClick={() => onMove(entry.key, 1)} aria-label={`Descendre ${entry.program.title}`}>
                    <ArrowDown aria-hidden="true" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon-xs" disabled={disabled} onClick={() => onRemove(entry.key)} aria-label={`Retirer ${entry.program.title}`}>
                    <X aria-hidden="true" />
                  </Button>
                </span>
              </li>
            ))}
          </ol>
        )}
        <select
          aria-label={`Ajouter dans ${role}`}
          className="tp-select"
          value=""
          disabled={disabled || choices.length === 0 || entries.length >= 100}
          onChange={event => onAdd(event.target.value)}
        >
          <option value="">{loading
            ? 'Chargement des habillages…'
            : choices.length === 0
              ? 'Aucun habillage disponible'
              : entries.length >= 100
                ? 'Limite de 100 habillages atteinte'
                : 'Ajouter un habillage…'}</option>
          {choices.map(program => (
            <option key={program.id} value={program.id}>{program.title} · {formatDuration(program.durationSec)}</option>
          ))}
        </select>
      </div>
    </div>
  )
})

const ReadOnlyTimelineRow = memo(function ReadOnlyTimelineRow({ item }: { item: ScheduleItem }) {
  return (
    <div className="agenda-rebroadcast__timeline-row is-readonly">
      <div className="agenda-rebroadcast__timeline-row-main">
        <strong>{scheduleItemTitle(item, ITEM_TYPE_LABELS[item.itemType])}</strong>
        <span>{ITEM_TYPE_LABELS[item.itemType]} · {formatDuration(item.durationSec)} · conservé</span>
      </div>
    </div>
  )
})

const RebroadcastOutcome = memo(function RebroadcastOutcome({ report, loading, windowValid, partialAllowed }: {
  report: RebroadcastReport | null
  loading: boolean
  windowValid: boolean
  partialAllowed: boolean
}) {
  if (!windowValid) {
    return <div className="agenda-rebroadcast__outcome is-idle">Saisissez une plage valide pour calculer le reste à remplir.</div>
  }
  if (!report) {
    return <div className="agenda-rebroadcast__outcome is-idle"><Spinner /> Calcul de la fin réelle…</div>
  }

  const noOccurrence = report.occurrences === 0
  const markerStarts = report.occurrenceStarts ?? []
  const tone = noOccurrence ? 'is-danger' : report.shortfallSec > 0 ? 'is-warning' : 'is-complete'
  return (
    <div className={`agenda-rebroadcast__outcome ${tone}${loading ? ' is-stale' : ''}`} aria-live="polite">
      <div className="agenda-rebroadcast__outcome-heading">
        <span><Clock3 aria-hidden="true" /> Bilan en temps réel</span>
        {loading ? <span><Spinner /> Recalcul…</span> : <strong>{report.occurrences} boucle{report.occurrences > 1 ? 's' : ''}</strong>}
      </div>
      <div className="agenda-rebroadcast__outcome-grid">
        <span><small>Fin souhaitée</small><strong>{antennaMoment(report.requestedEnd, report.targetStart)}</strong></span>
        <span><small>Fin réelle</small><strong>{antennaMoment(report.endsAt, report.targetStart)}</strong></span>
        <span className="agenda-rebroadcast__remaining"><small>Reste à remplir</small><strong>{exactDuration(report.shortfallSec)}</strong></span>
      </div>
      {report.cadenceSec != null && markerStarts.length > 0 && (
        <div className="agenda-rebroadcast__markers">
          <small>Départs sur les horaires repères</small>
          <div>
            {markerStarts.slice(0, 12).map(start => <time key={start}>{antennaMoment(start, report.targetStart)}</time>)}
            {markerStarts.length > 12 && <span>+ {markerStarts.length - 12} autres</span>}
          </div>
          <em>
            Cadence {exactDuration(report.cadenceSec)} · {exactDuration(report.cadencePaddingSec)} d’infoservice ajouté au total
          </em>
        </div>
      )}
      {noOccurrence && (
        <p><TriangleAlert aria-hidden="true" /> {report.stopReason === 'CONFLICT'
          ? 'Le premier passage rencontre un élément déjà posé.'
          : partialAllowed
            ? 'Même écourtée, la boucle ne place aucun élément dans la fenêtre.'
            : 'La boucle complète ne tient pas dans la fenêtre choisie.'}</p>
      )}
      {!noOccurrence && report.shortfallSec === 0 && <p>La rediffusion rejoint exactement la fin souhaitée.</p>}
      {!noOccurrence && report.shortfallSec > 0 && <p>Il restera {exactDuration(report.shortfallSec)} à compléter après la dernière rediffusion.</p>}
    </div>
  )
})

export default RebroadcastDialog
